import { AxiosError } from "axios"

/**
 * Интерфейс стандартизированной ошибки приложения
 */
export interface AppError {
	message: string
	statusCode?: number
	code?: string
	originalError?: unknown
}

/**
 * Коды ошибок приложения
 */
export enum ErrorCode {
	UNAUTHORIZED = "UNAUTHORIZED",
	FORBIDDEN = "FORBIDDEN",
	NOT_FOUND = "NOT_FOUND",
	SERVER_ERROR = "SERVER_ERROR",
	NETWORK_ERROR = "NETWORK_ERROR",
	VALIDATION_ERROR = "VALIDATION_ERROR",
	UNKNOWN = "UNKNOWN",
}

/**
 * Обработчик ошибок API с поддержкой различных типов ошибок
 */
export const handleApiError = (error: unknown): AppError => {
	// Axios ошибка
	if (error instanceof AxiosError) {
		const statusCode = error.response?.status
		const message =
			error.response?.data?.message ||
			error.response?.data?.error ||
			error.message

		// Специальная обработка для разных HTTP статусов
		switch (statusCode) {
			case 400:
				return {
					message: message || "Неверный запрос",
					statusCode,
					code: ErrorCode.VALIDATION_ERROR,
					originalError: error,
				}

			case 401:
				return {
					message: "Необходима авторизация",
					statusCode,
					code: ErrorCode.UNAUTHORIZED,
					originalError: error,
				}

			case 403:
				return {
					message: "Доступ запрещен",
					statusCode,
					code: ErrorCode.FORBIDDEN,
					originalError: error,
				}

			case 404:
				return {
					message: "Ресурс не найден",
					statusCode,
					code: ErrorCode.NOT_FOUND,
					originalError: error,
				}

			case 422:
				return {
					message: message || "Ошибка валидации данных",
					statusCode,
					code: ErrorCode.VALIDATION_ERROR,
					originalError: error,
				}

			case 500:
			case 502:
			case 503:
			case 504:
				return {
					message: "Ошибка сервера. Попробуйте позже",
					statusCode,
					code: ErrorCode.SERVER_ERROR,
					originalError: error,
				}

			default:
				// Проверка на network error
				if (!error.response) {
					return {
						message: "Ошибка сети. Проверьте подключение к интернету",
						code: ErrorCode.NETWORK_ERROR,
						originalError: error,
					}
				}

				return {
					message: message || "Произошла ошибка",
					statusCode,
					code: ErrorCode.UNKNOWN,
					originalError: error,
				}
		}
	}

	// Стандартная JS ошибка
	if (error instanceof Error) {
		return {
			message: error.message,
			code: ErrorCode.UNKNOWN,
			originalError: error,
		}
	}

	// Неизвестный тип ошибки
	return {
		message: "Неизвестная ошибка",
		code: ErrorCode.UNKNOWN,
		originalError: error,
	}
}

/**
 * Определяет, требует ли ошибка повторной авторизации
 */
export const isAuthError = (error: AppError): boolean => {
	return error.code === ErrorCode.UNAUTHORIZED
}

/**
 * Определяет, является ли ошибка сетевой
 */
export const isNetworkError = (error: AppError): boolean => {
	return error.code === ErrorCode.NETWORK_ERROR
}

/**
 * Определяет, является ли ошибка серверной
 */
export const isServerError = (error: AppError): boolean => {
	return error.code === ErrorCode.SERVER_ERROR
}

/**
 * Форматирует ошибку для отображения пользователю
 */
export const formatErrorMessage = (error: unknown): string => {
	const appError = handleApiError(error)
	return appError.message
}

/**
 * Логирует ошибку (в production отправлять в Sentry)
 */
export const logError = (error: unknown, context?: string) => {
	const appError = handleApiError(error)

	// В development режиме логируем в консоль
	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line no-console
		console.error(`[Error${context ? ` in ${context}` : ""}]:`, {
			message: appError.message,
			code: appError.code,
			statusCode: appError.statusCode,
			originalError: appError.originalError,
		})
	}

	// TODO: В production отправлять в Sentry/LogRocket
	// if (process.env.NODE_ENV === 'production') {
	//   Sentry.captureException(appError.originalError, {
	//     tags: { code: appError.code },
	//     contexts: { error: { message: appError.message } },
	//   })
	// }
}



